/**
 * Block 12 PR-D-A Phase D5 Step 9 — IDB autosave layer для quote drafts.
 *
 * Persistent mirror Zustand store'а (`useQuoteDraftStore`). Хранит черновики
 * keyed by `draftKey` (string — projectId либо 'standalone'). Переживает
 * F5 refresh, закрытие/открытие браузера, cross-tab (last-writer-wins).
 *
 * **Отдельная DB `quote-drafts`** (не пересекается с `pomoshnik-offline-v1`
 * (методики/projects/quotes/pendingMedia) и `calculator-drafts` (anketa drafts)).
 *
 *   1. Schema isolation — добавление/удаление quote drafts store не требует
 *      version bump main DB.
 *   2. Lifecycle isolation — `clearAllQuoteDraftsFromIdb()` (logout) не трогает
 *      saved quotes (`pomoshnik-offline-v1.quotes` store) и anketa drafts.
 *   3. Mirror pattern hotfix-2C C5 `calculator-drafts` для consistency.
 *
 * **Graceful degradation:** if `indexedDB === undefined` (SSR / private
 * browsing / quota exhausted) — функции возвращают `undefined` для load,
 * no-op для save/clear. НЕ throw — UI продолжает работу без durability.
 *
 * Reference: Block_12_PR_D_A_2026-05-17_start_v2.md Phase D5 Step 9 +
 * mirror `calculatorIdbSync.ts` (hotfix-2C C5 Step 11).
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';
import type { QuoteState } from '../types';

export const QUOTE_DRAFTS_DB_NAME = 'quote-drafts';
export const QUOTE_DRAFTS_DB_VERSION = 1;
export const QUOTE_DRAFTS_STORE = 'drafts';

export interface QuoteDraftsDBSchema extends DBSchema {
  drafts: {
    key: string; // draftKey: projectId | 'standalone'
    value: QuoteState;
  };
}

let dbPromise: Promise<IDBPDatabase<QuoteDraftsDBSchema>> | null = null;

function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function logWarn(scope: string, err: unknown): void {
  if (import.meta.env.DEV) {
    console.warn(`[quoteDraftIdbSync] ${scope} failed`, err);
  }
}

async function getDb(): Promise<IDBPDatabase<QuoteDraftsDBSchema> | null> {
  if (!isIdbAvailable()) return null;
  if (!dbPromise) {
    dbPromise = openDB<QuoteDraftsDBSchema>(QUOTE_DRAFTS_DB_NAME, QUOTE_DRAFTS_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(QUOTE_DRAFTS_STORE)) {
          db.createObjectStore(QUOTE_DRAFTS_STORE);
        }
      },
      terminated() {
        dbPromise = null;
      },
      blocking() {
        dbPromise?.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
    });
  }
  try {
    return await dbPromise;
  } catch (err) {
    logWarn('openDB', err);
    dbPromise = null;
    return null;
  }
}

export async function saveQuoteDraftToIdb(key: string, state: QuoteState): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.put(QUOTE_DRAFTS_STORE, state, key);
  } catch (err) {
    logWarn('saveQuoteDraftToIdb', err);
  }
}

export async function loadQuoteDraftFromIdb(key: string): Promise<QuoteState | undefined> {
  const db = await getDb();
  if (!db) return undefined;
  try {
    return await db.get(QUOTE_DRAFTS_STORE, key);
  } catch (err) {
    logWarn('loadQuoteDraftFromIdb', err);
    return undefined;
  }
}

export async function clearQuoteDraftFromIdb(key: string): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.delete(QUOTE_DRAFTS_STORE, key);
  } catch (err) {
    logWarn('clearQuoteDraftFromIdb', err);
  }
}

export async function clearAllQuoteDraftsFromIdb(): Promise<void> {
  const db = await getDb();
  if (!db) return;
  try {
    await db.clear(QUOTE_DRAFTS_STORE);
  } catch (err) {
    logWarn('clearAllQuoteDraftsFromIdb', err);
  }
}

/** Для тестов: сбрасывает singleton connection. */
export function _resetQuoteDraftIdbCacheForTests(): void {
  dbPromise = null;
}
